/**
 * Setup Flow Tracking API
 *
 * Functions for tracking user progress through the setup flow.
 * Stores progress in Supabase profiles table for analytics and auto-resume.
 */

import { supabase } from './client'
import type { SetupFlowStep, SetupFlowProgress } from '../types/setupFlow'

/**
 * Update user's setup flow progress
 *
 * @param step - Current setup flow step
 * @param onboardingSlideReached - Highest slide reached (optional)
 * @param onboardingSlideViewed - Slide that was just viewed (optional)
 * @returns Success status and optional error message
 */
export async function updateSetupFlowProgress(
  step: SetupFlowStep,
  onboardingSlideReached?: number,
  onboardingSlideViewed?: number
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return { success: false, error: 'No authenticated user' }
    }

    const updateData: any = {
      setup_flow_step: step,
      updated_at: new Date().toISOString(),
    }

    // Update onboarding slide reached if provided
    if (onboardingSlideReached !== undefined) {
      updateData.onboarding_slide_reached = onboardingSlideReached
    }

    // If step is 'completed', mark as completed
    if (step === 'completed') {
      updateData.setup_flow_completed = true
      updateData.setup_flow_completed_at = new Date().toISOString()
    }

    // Check if profile exists and get current slides viewed
    const { data: existingProfile, error: fetchError } = await supabase
      .from('profiles')
      .select('id, onboarding_slides_viewed')
      .eq('id', user.id)
      .single()

    if (fetchError && fetchError.code !== 'PGRST116') {
      throw fetchError
    }

    // Handle onboarding slide viewed tracking
    if (onboardingSlideViewed !== undefined && existingProfile) {
      const currentSlidesViewed = existingProfile.onboarding_slides_viewed || []

      // Add slide to array if not already present
      if (!currentSlidesViewed.includes(onboardingSlideViewed)) {
        updateData.onboarding_slides_viewed = [...currentSlidesViewed, onboardingSlideViewed]
      }
    }

    if (existingProfile) {
      // UPDATE existing profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)

      if (updateError) throw updateError
    } else {
      // INSERT new profile (should rarely happen, but handle it)
      const insertData: any = {
        id: user.id,
        ...updateData,
        setup_flow_started_at: new Date().toISOString(),
      }

      // For insert, set slides viewed as array with single element
      if (onboardingSlideViewed !== undefined) {
        insertData.onboarding_slides_viewed = [onboardingSlideViewed]
      }

      const { error: insertError } = await supabase
        .from('profiles')
        .insert(insertData)

      if (insertError) throw insertError
    }

    return { success: true }
  } catch (error: any) {
    console.error('[updateSetupFlowProgress] Error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get user's current setup flow progress
 *
 * @returns Setup flow progress data, or null if not found
 */
export async function getSetupFlowProgress(): Promise<SetupFlowProgress | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null

    const { data, error } = await supabase
      .from('profiles')
      .select('setup_flow_step, setup_flow_completed, setup_flow_started_at, setup_flow_completed_at, onboarding_slide_reached, onboarding_slides_viewed')
      .eq('id', user.id)
      .single()

    if (error) throw error
    if (!data) return null

    return {
      step: (data.setup_flow_step || 'login') as SetupFlowStep,
      completed: data.setup_flow_completed || false,
      startedAt: data.setup_flow_started_at || new Date().toISOString(),
      completedAt: data.setup_flow_completed_at || undefined,
      onboardingSlideReached: data.onboarding_slide_reached || 0,
      onboardingSlidesViewed: data.onboarding_slides_viewed || [],
    }
  } catch (error: any) {
    console.error('[getSetupFlowProgress] Error:', error)
    return null
  }
}

/**
 * Mark setup flow as started
 * Called after successful login/register
 *
 * IMPORTANT: This function RESETS the setup flow to 'login' state
 * This ensures that after OTP verification, users always start fresh
 *
 * @returns void
 */
export async function startSetupFlow(): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    // Check if profile exists
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('id, setup_flow_started_at')
      .eq('id', user.id)
      .single()

    // Ignore "not found" error (profile doesn't exist yet)
    if (fetchError && fetchError.code !== 'PGRST116') {
      throw fetchError
    }

    if (!profile) {
      // Profile doesn't exist → CREATE new profile with initial setup state
      await supabase.from('profiles').insert({
        id: user.id,
        setup_flow_step: 'login',
        setup_flow_started_at: new Date().toISOString(),
        setup_flow_completed: false,
        onboarding_slide_reached: 0,
        onboarding_slides_viewed: [],
      })
    } else {
      // Profile exists → RESET setup flow to 'login' (important for re-registration)
      const updateData: any = {
        setup_flow_step: 'login',
      }

      // Set started_at only if not already set (preserve original start time)
      if (!profile.setup_flow_started_at) {
        updateData.setup_flow_started_at = new Date().toISOString()
      }

      await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)
    }
  } catch (error) {
    console.error('[startSetupFlow] Error:', error)
  }
}
